/**
 * Segment menu: user, event and multi cohort segment selection
 */
import flatMap from 'lodash/flatMap';

import {renameProperty} from 'common/report/util/properties';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {pluralize} from 'common/util/string';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';

import {IPropertiesMenu, PropertyItem} from '../properties-menu/types';
import {ChildMenu} from '../child-menu';
import {propertyItemToSegmentation, segmentationToPropertyItem} from '../properties-menu/util';
import {getCohortFromPropertyItem} from '../util';
import {CohortSegment, isEmptySegmentSelection, SavedCohortSegment, Segment} from './types';
import template from './index.jade';

interface SegmentMenuState {
  selected: Segment;
}

export class SegmentMenu extends ChildMenu<SegmentMenuState> {
  private propertiesMenu: IPropertiesMenu;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'enable-workspace-filter-restrictions': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
      'other-group-by-options': {
        type: `string`,
        default: `[]`,
        description: `enables breakdown by other keys`,
      },
    };
  }

  get config() {
    return {
      template,

      helpers: {
        ...super.config.helpers,
        preventTracking,
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleSelect: ({detail: {item, rawItem, commit, autoTriggered}}) => {
          const source = Array.isArray(item) ? item[0]?.resourceType : item.resourceType;
          this.handleSelect(item, {rawItem, source, commit, autoTriggered});
        },
        handleSelectOption: (ev) => (this.propertiesMenu.useMultipleSelection = ev.detail.selected === `cohort`),
        isCohortSelection: () =>
          this.isOnCohortsTab() || this.getSelectionType(this.attr(`selected`)) === ResourceType.Cohort,
        onMenuInserted: (vnode) => requestAnimationFrame(() => (this.propertiesMenu = vnode.elm)),

        /** Provides a selection value for the underlying menu */
        getMenuSelection: () => JSON.stringify(this.transformSelection(this.attr(`selected`))),

        /** Since mp-properties-menu defines when content is searchable, trigger a manual render */
        handleStagedProperty: () => this.update(),
      },
    };
  }

  constructor() {
    super();

    this.propertiesMenu = null;
  }

  public shouldCloseOnSelect(detail: any) {
    return this.isOnCohortsTab() ? detail.item.length === 0 : true;
  }

  public isContentSearchable() {
    return this.propertiesMenu ? this.propertiesMenu.isContentSearchable() : true;
  }

  public selectedIcon() {
    const {selected} = this.state;
    const selectionType = this.getSelectionType(selected);

    switch (selectionType) {
      case ResourceType.User: {
        const segment = selected[0][selectionType];
        return segment.dataGroupId ? `group-attribute` : `profile-attribute`;
      }
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default:
        return null;
    }
  }

  public selectedLabel() {
    // TODO(pablo): use attributes instead of state
    // selected state must be used here since the child menu and
    // query block do not render in the same frame, whenever query block
    // receives new attributes and re-renders, the child menu does not
    // have the updated attributes so label computation is one frame behind
    // updateSync can be used in query-block but there must be a better way
    const {selected} = this.state;
    const selectionType = this.getSelectionType(selected);
    let label = ``;

    // selected labels are based on single segment selections
    switch (selectionType) {
      case ResourceType.Cohort: {
        const length = selected[0].length;
        label = `${length} ${pluralize(`cohort`, length)}`;
        break;
      }
      case ResourceType.User:
      case ResourceType.Event: {
        const selectedProperty = selected[0][selectionType];
        label = renameProperty(selectedProperty.propertyName, {
          dataGroupId: selectedProperty.dataGroupId,
          customProperty: selectedProperty.customProperty,
        });
        if (selectedProperty.unit) {
          label = `${label} (${TimeUnitLabels[selectedProperty.unit]})`;
        }
        break;
      }
      case ResourceType.Other:
        label = selected[0][selectionType].label;
        break;
      default:
        label = `Select a property`;
        break;
    }

    return label;
  }

  private isOnCohortsTab(): boolean {
    return this.propertiesMenu && this.propertiesMenu.getSelectedTab() === `cohort`;
  }

  private handleSelect(
    item: PropertyItem | PropertyItem[],
    {
      rawItem,
      source,
      commit = false,
      autoTriggered = false,
    }: {
      rawItem?: PropertyItem;
      source?: ResourceType;
      commit?: boolean;
      autoTriggered?: boolean;
    },
  ) {
    const resourceType = this.isOnCohortsTab() ? ResourceType.Cohort : source;
    const selection = this.getSelectionObject(item, resourceType);
    const trackingProps = this.trackingProps(resourceType, selection, rawItem);

    let queryReason: QueryReason;
    if (autoTriggered) {
      queryReason = QueryReason.QbAutoTriggered;
    } else if (isEmptySegmentSelection(selection)) {
      queryReason = QueryReason.QbOtherUpdate;
    } else {
      queryReason = QueryReason.QbBreakdownUpdate;

      trackLearningAction({
        projectId: this.attr(`project-id`),
        reportName: this.attr(`track-name`),
        actionType: ActionType.Breakdown,
        resourceType,
      });

      this.trackSelection({
        eventName: `Select segment`,
        props: trackingProps,
      });
    }

    const detail = {item: selection, trackingProps, rawItem, commit, queryReason};
    this.dispatchEvent(new CustomEvent(`select`, {detail}));
  }

  /**
   * Generates a Segment for a list of cohort ids or an actual event/user property
   */
  private getSelectionObject(selection: PropertyItem | PropertyItem[], source: ResourceType): Segment[] {
    switch (source) {
      case ResourceType.Cohort: {
        selection = Array.isArray(selection) ? selection : [selection];
        return selection.length ? [selection.map((item) => getCohortFromPropertyItem(item))] : [];
      }
      case ResourceType.User:
        return [{[ResourceType.User]: propertyItemToSegmentation(selection, ResourceType.User)}];
      case ResourceType.Event:
        return [{[ResourceType.Event]: propertyItemToSegmentation(selection, ResourceType.Event)}];
      case ResourceType.Other:
        return [{[ResourceType.Other]: this.generateOtherSegment(selection)}];
      default:
        throw new Error(`unknown segment type: ${source}`);
    }
  }

  private generateOtherSegment(item) {
    return {label: item.label, value: item.value, resourceType: item.resourceType};
  }

  /**
   * Infers selection type from the first selected segment
   * TODO: this will change in the future for multi-segment
   */
  private getSelectionType(selection: Segment[]): ResourceType {
    let selectionType = null;
    const hasSelected = Array.isArray(selection) && selection.length && selection[0];

    if (hasSelected) {
      const selectionItem = flatMap(selection)[0];
      selectionType = Object.values(ResourceType).find((resourceType) => resourceType in selectionItem) || null;
    }

    return selectionType;
  }

  /**
   * Transforms the selected format to
   * the format used in mp-properties-menu
   */
  private transformSelection(selection: Segment[]) {
    const selectionType = this.getSelectionType(selection);

    switch (selectionType) {
      case ResourceType.Cohort:
        return flatMap(selection[0], (entry: CohortSegment) => ({
          resourceType: ResourceType.Cohort,
          value:
            (entry as SavedCohortSegment).cohort && ((entry as SavedCohortSegment).cohort.id || {cohort: entry.cohort}), // id does not exist on inline cohorts,
          label: entry.cohort.name,
        }));
      case ResourceType.User:
      case ResourceType.Event: {
        return segmentationToPropertyItem(selection[0][selectionType]);
      }
      case ResourceType.Other: {
        return selection[0][selectionType];
      }
      default:
        return null;
    }
  }

  /**
   * Generates tracking props for the selected segment
   */
  private trackingProps(source: ResourceType, selection: Segment[], rawItem?: PropertyItem) {
    const transformedSelection = this.transformSelection(selection);
    let extraProps = {};
    let segmentName = ``;
    let isCustomPropertySegment = false;

    switch (source) {
      case ResourceType.Cohort:
        segmentName = `cohorts`;
        if (rawItem) {
          extraProps = {
            'cohort type': rawItem.createdBy
              ? rawItem.createdBy.id === this.attr(`user-id`)
                ? `cohorts-yours`
                : `cohorts`
              : `inline cohorts`,
          };
        }
        break;
      case ResourceType.User:
      case ResourceType.Event: {
        const prop = selection[0][source];
        segmentName = prop.propertyName;
        isCustomPropertySegment = !!(prop.customProperty || prop.customPropertyId);
        extraProps = {
          'Property data type': prop.propertyType,
          'Property default type': prop.propertyDefaultType,
        };
        break;
      }
      case ResourceType.Other:
        segmentName = `Other: ${selection[0][source]}`;
        break;
      default:
        break;
    }

    return {
      ...extraProps,
      'Segment type': source,
      'Segment name': segmentName,
      'Segments length': Array.isArray(transformedSelection) ? transformedSelection.length : 1,
      'Is segment custom property': isCustomPropertySegment,
    };
  }

  getSelectedHoverTooltipData(): ContextPanelData {
    return this.propertiesMenu && this.propertiesMenu.getSelectedHoverTooltipData();
  }

  getSelectedTrackingProps() {
    return this.propertiesMenu && this.propertiesMenu.getSelectedTrackingProps();
  }

  handleTooltipContextAction(ev: Event) {
    return this.propertiesMenu && this.propertiesMenu.handleTooltipContextAction(ev);
  }

  handleCloseMenu() {
    this.propertiesMenu && this.propertiesMenu.handleCloseMenu();
  }
}

export const SegmentMenuEl = defineAndMakeJsxFactory(`mp-segment-menu`, SegmentMenu);
